// Third-party libraries
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Grid,
  Container,
  Stack,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Backdrop,
  Typography,
  Box,
} from "@mui/material";

// Local modules
import { AppContext } from "../../utils/stateHandlers/contexts";
import api from "../../utils/api";
import formatting from "../../utils/formatHelpers";
import StringConstants from "../../utils/constants";
import { Mobile, Default } from "../../utils/deviceHelpers";
import MediaPanel from "./controls/MediaPanel";

export default function BlankDiamondDetails(props) {
  const { setShowFooter, setShowHeader } = useContext(AppContext);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [diamond, setDiamond] = useState({});
  const [fields, setFields] = useState({});
  const [diamondUnavailable, setDiamondUnavailable] = useState(false);
  const mined = StringConstants.DIAMOND_MINED_OR_LAB_MINED_DESC;
  const lab = StringConstants.DIAMOND_MINED_OR_LAB_LAB_DESC;

  useEffect(() => {
    let isMounted = true;
    setPageLoaded(false);

    const loadDiamondData = async () => {
      try {
        const [diamondResponse, fieldsResponse] = await Promise.all([
          api.getRequest(`diamonds/detail/${props.match.params.HooverStockId}`),
          api.getRequest("diamonds/fields"),
        ]);

        if (!isMounted) {
          return;
        }

        const diamondData = diamondResponse.data;
        setDiamond(diamondData);
        setDiamondUnavailable(false);

        setFields(fieldsResponse.data);
        setPageLoaded(true);
      } catch (error) {
        setDiamond({});
        setDiamondUnavailable(true);
        if (!isMounted) {
          return;
        }
        setPageLoaded(true);
      }
    };

    loadDiamondData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowFooter(false);
    setShowHeader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDisplayValue = (value, fallback = "None") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "string" && value.trim() === "") {
      return fallback;
    }

    return value;
  };

  const formatHeadlineText = () => {
    const caratValue = getDisplayValue(diamond.Carat);
    const shapeValue = getDisplayValue(diamond.Shape);
    const caratText = caratValue === "None" ? "None" : `${caratValue}ct`;
    const shapeText = shapeValue === "None" ? "None" : `${shapeValue} Shape`;

    if (caratText === "None" && shapeText === "None") {
      return "None";
    }

    return `${caratText} ${shapeText}`.trim();
  };

  const buildDetailLine = () => {
    const caratValue = getDisplayValue(diamond.Carat);
    const shapeValue = getDisplayValue(diamond.Shape);
    const colorValue = getDisplayValue(diamond.Color || diamond.FancyColor);
    const clarityValue = getDisplayValue(diamond.Clarity);
    const certificationValue = getDisplayValue(diamond.HooverCertification);
    const caratText = caratValue === "None" ? "None" : `${caratValue}ct`;
    const certificationText =
      certificationValue === "None"
        ? "None"
        : `${certificationValue} Certified`;

    const parts = [
      caratText,
      shapeValue,
      colorValue,
      clarityValue,
      certificationText,
    ]
      .filter((part) => part !== undefined && part !== null)
      .map((part) => (typeof part === "string" ? part.trim() : part))
      .filter((part) => part !== "");

    if (!parts.some((part) => part !== "None")) {
      return "None";
    }

    return parts.join(" ");
  };

  const resolveFieldValue = (field) => {
    if (!field) {
      return null;
    }

    if (field.name === "LWRatio" || field.label === "L/W Ratio") {
      return (
        diamond?.[field.name] ?? diamond?.LWRatio ?? diamond?.["L/W Ratio"]
      );
    }

    return diamond?.[field.name];
  };

  const skuDisplay = getDisplayValue(diamond.HooverStockId);
  const filteredSpecificationFields = useMemo(() => {
    if (!Array.isArray(fields)) {
      return [];
    }

    const shape = diamond?.Shape ?? "";
    const color = diamond?.Color ?? "";
    const girdle = diamond?.Girdle ?? "";
    const culet = diamond?.Culet ?? "";

    const shouldHideGirdle =
      shape === "Round" || shape === "Heart" || girdle === "";
    const shouldHideCulet =
      shape === "Round" || shape === "Heart" || culet === "";
    const showColorField = color !== "";
    const fancyColorFieldNames = new Set([
      "FancyColor",
      "FancyColorIntensity",
      "FancyColorOvertone",
    ]);
    const price = new Set([
      "CalcPercentOffRap", 
      "HooverPrice", 
      "CalcPricePerCarat"
    ]);

    return fields
      .filter((field) => field.details_order !== null)
      .filter(
        (field) => field.name !== "ImageLink" && field.name !== "VideoLink"
      )
      .filter((field) => {
        if (field.name === "Girdle") {
          return !shouldHideGirdle;
        }

        if (field.name === "Culet") {
          return !shouldHideCulet;
        }

        const fieldValue = resolveFieldValue(field);

        if (field.name === "LWRatio" || field.label === "L/W Ratio") {
          if (
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === "" ||
            fieldValue === "-"
          ) {
            return false;
          }
        }

        if (!showColorField && field.name === "Color") {
          return false;
        }

        if (showColorField && fancyColorFieldNames.has(field.name)) {
          return false;
        }

        if (fieldValue === null || fieldValue === undefined) {
          return false;
        }

        return true;
      })
      .filter((field) => !price.has(field.name))
      .sort((a, b) => a.details_order - b.details_order);
  }, [fields, diamond]);

  const renderSpecificationValue = (field) => {
    const rawValue = resolveFieldValue(field);
    const certificationLink = diamond?.CertificationLocation;
    const formattingFn = field.format ? formatting[field.format] : null;
    const formattedValue = formattingFn ? formattingFn(rawValue) : rawValue;
    const displayValue = getDisplayValue(formattedValue);

    if (field.label === "Report" && certificationLink) {
      return displayValue === "None" ? (
        displayValue
      ) : (
        <>
          {displayValue}&nbsp;&nbsp;
          <a
            href={certificationLink}
            className="underline"
            target="_blank"
            rel="noreferrer"
          >
            View
          </a>
        </>
      );
    }

    return displayValue;
  };

  const specificationsBlock = () => (
    <Stack>
      <Typography variant="h5">Specifications:</Typography>
      <Divider />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(2, 1fr)",
          },
        }}
      >
        <Typography variant="body3" sx={{ padding: ".5rem" }}>
          {formatHeadlineText()}
        </Typography>

        {filteredSpecificationFields.map((field) => (
          <Typography
            key={field.name}
            variant="body3"
            sx={{ padding: ".5rem" }}
          >
            {field.label}: {renderSpecificationValue(field)}
          </Typography>
        ))}
      </Box>
    </Stack>
  );

  return (
    <>
      {!pageLoaded ? (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <Container maxWidth="md">
          {diamondUnavailable ? (
            <Stack spacing={2} paddingY="4rem" alignItems="center">
              <Typography variant="articleTitle" textAlign="center">
                This item is not available at this time
              </Typography>
            </Stack>
          ) : (
            <>
              <Mobile>
                <Stack spacing={1}>
                  <h2 className="text-align-center">
                    {`${diamond.MinedOrLab === "L" ? lab : mined} Diamond`}
                    <span className="sku">SKU: {skuDisplay}</span>
                  </h2>
                  <h2 className="text-align-center">{buildDetailLine()}</h2>
                  <Divider orientation="horizontal" flexitem="true" />
                  <span style={{ justifyContent: "center" }}>
                    <MediaPanel
                      diamond={diamond}
                      popover={props.popover}
                      context="side_panel"
                    />
                  </span>
                </Stack>
              </Mobile>
              <Default>
                <Stack justifyContent="center">
                  <Typography variant="articleTitle">
                    {`${diamond.MinedOrLab === "L" ? lab : mined} Diamond`}
                    <Typography variant="h3" className="sku">
                      SKU: {skuDisplay}
                    </Typography>
                  </Typography>
                  <Typography variant="h6">{buildDetailLine()}</Typography>
                  <Divider
                    flexItem
                    sx={{ marginBottom: "2rem", marginTop: "2rem" }}
                  />
                  <Stack direction="row" justifyContent="center" spacing={3}>
                    <Stack>
                      <MediaPanel diamond={diamond} popover={props.popover} />
                    </Stack>
                  </Stack>
                </Stack>
              </Default>
              <Grid container rowSpacing={2} justifyContent="center">
                <Grid item className="details-container">
                  <Card variant="outlined">
                    <CardContent className="additional-info">
                      {specificationsBlock()}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Container>
      )}
    </>
  );
}
